import { DialogTitle, Tab } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { memo, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import * as SX from './sx';
import type { BulkFilterValidationsDialogProps as Props } from './types';
import { ValidationInfoItem } from './validation-info-item';

export const BulkFilterValidationsDialog = memo(
  ({ className, state, validationResult, invalidItemsCount, items }: Props) => {
    const [tab, setTab] = useState('invalids');

    const invalidsCount = useMemo(() => {
      return Object.values(validationResult || {}).reduce((acc, result) => {
        return acc + (result.invalids?.length ?? 0);
      }, 0);
    }, [validationResult]);

    const duplicatesCount = useMemo(() => {
      return Object.values(validationResult || {}).reduce((acc, result) => {
        return acc + (result.duplicates?.length ?? 0);
      }, 0);
    }, [validationResult]);

    useEffect(() => {
      if (!state.value) return;
      if (!invalidsCount) {
        setTab('duplicates');
      } else if (!duplicatesCount) {
        setTab('invalids');
      }
    }, [invalidsCount, duplicatesCount]);

    const handleChangeTab = (event: SyntheticEvent, newValue: string) => {
      setTab(newValue);
    };

    return (
      <Dialog
        open={state.value}
        fullWidth={true}
        maxWidth="xs"
        onClose={state.onFalse}
        className={classnames('BulkFilterValidationsDialog', className)}
        sx={SX.root}
      >
        <DialogTitle
          title={`Total Unrecognized Entries (${invalidItemsCount})`}
          icon="eye-outlined"
          onClose={state.onFalse}
        />

        <DialogContent>
          <TabContext value={tab}>
            <TabList onChange={handleChangeTab} size="medium">
              <Tab
                LabelContent={
                  <Typography variant="body5" fontWeight={700}>
                    {invalidsCount}
                  </Typography>
                }
                key={'invalids'}
                label={
                  <Typography variant="body3" fontWeight={600}>
                    Unrecognized Entries
                  </Typography>
                }
                value={'invalids'}
                LabelVariant="soft"
                disabled={!invalidsCount}
              />
              <Tab
                LabelContent={
                  <Typography variant="body5" fontWeight={700}>
                    {duplicatesCount}
                  </Typography>
                }
                key={'duplicates'}
                label={
                  <Typography variant="body3" fontWeight={600}>
                    Duplicated Entries
                  </Typography>
                }
                value={'duplicates'}
                LabelVariant="soft"
                disabled={!duplicatesCount}
              />
            </TabList>
            <TabPanel value="invalids" key="invalids" sx={SX.tabPanel}>
              <Box display="flex" flexDirection="column" gap={1}>
                {items?.map((subItem) => (
                  <ValidationInfoItem
                    key={subItem.field}
                    title={subItem.name}
                    items={validationResult?.[subItem.field!]?.invalids ?? []}
                  />
                ))}
              </Box>
            </TabPanel>
            <TabPanel value="duplicates" key="duplicates" sx={SX.tabPanel}>
              <Box display="flex" flexDirection="column" gap={1}>
                {items?.map((subItem) => (
                  <ValidationInfoItem
                    key={subItem.field}
                    title={subItem.name}
                    items={validationResult?.[subItem.field!]?.duplicates ?? []}
                  />
                ))}
              </Box>
            </TabPanel>
          </TabContext>
        </DialogContent>

        <DialogActions>
          <Button size="small" color="default" onClick={state.onFalse}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

BulkFilterValidationsDialog.displayName = 'BulkFilterValidationsDialog';
